import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart, BarChart, Bar, Cell, ReferenceLine } from "recharts";
import { Activity, Pause, Play, RefreshCw, CircleDot, Download, Shield, Clock, TrendingUp, TrendingDown, ArrowUp, Wallet, Terminal, Search, Cpu, Database, Rocket, Copy, Check, CalendarDays, List, Target, Bell, BarChart3, Zap, Brain, AlertTriangle, Trophy } from "lucide-react";
import PerformanceTab from "./PerformanceTab";
import DealLogTab from "./DealLogTab";
import { LiveCandlestickChart, DealProfitGauge } from "./LiveTradingWidgets";
import AutopilotTab from "./AutopilotTab";
import StrategyLabTab from "./StrategyLabTab";
import SkynetBrainTab from "./SkynetBrainTab";
import CodexRiskDashboard from "./CodexRiskDashboard";
import VirtualPortfolioTab from "./VirtualPortfolioTab";
import DealsDashboard from "./DealsDashboard";
import LabEngineTab from "./LabEngineTab";
import MagaziTab from "./MagaziTab";
import PortfoliosTab from "./PortfoliosTab";
import ArenaTab from "./ArenaTab";
import EtfAuditTab from "./EtfAuditTab";

import { API_BASE as API } from '../config';

// Animated number component
function AnimNum({ value, prefix = "", suffix = "", decimals = 2, color = "text-zinc-100", testId }) {
  const [display, setDisplay] = useState(value);
  const [flash, setFlash] = useState("");
  const prev = useRef(value);

  useEffect(() => {
    if (value !== prev.current) {
      const diff = value - prev.current;
      setFlash(diff > 0 ? "flash-green" : diff < 0 ? "flash-red" : "");
      setTimeout(() => setFlash(""), 800);

      // Animate to new value
      const steps = 20;
      const inc = (value - display) / steps;
      let step = 0;
      const iv = setInterval(() => {
        step++;
        setDisplay(d => d + inc);
        if (step >= steps) {
          clearInterval(iv);
          setDisplay(value);
        }
      }, 30);
      prev.current = value;
      return () => clearInterval(iv);
    }
  }, [value]);

  return (
    <span className={`${color} ${flash} transition-colors duration-500`} data-testid={testId}>
      {prefix}{typeof display === 'number' ? display.toFixed(decimals) : display}{suffix}
    </span>
  );
}

function LiveStat({ label, children, sub, testId }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-lg p-4 hover:border-zinc-700 transition-all duration-300" data-testid={testId}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5">{label}</div>
      <div className="text-xl font-mono font-semibold">{children}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-1 font-mono">{sub}</div>}
    </div>
  );
}

export default function EuBotDashboard() {
  const { token } = useAuth();
  const [tab, setTab] = useState("magazi");
  const [botStatus, setBotStatus] = useState(null);
  const [cmdLoading, setCmdLoading] = useState(false);
  const [priceHistory, setPriceHistory] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [pulse, setPulse] = useState(false);
  const [priceDir, setPriceDir] = useState(0);
  const prevPrice = useRef(0);
  const [equityHistory, setEquityHistory] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [dealInsight, setDealInsight] = useState(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [allBots, setAllBots] = useState([]);
  const [shadowBots, setShadowBots] = useState([]);
  const [injectAmount, setInjectAmount] = useState("");
  const [injectResult, setInjectResult] = useState(null);
  const [injectLoading, setInjectLoading] = useState(false);
  const [cmdResult, setCmdResult] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [lifetimeStats, setLifetimeStats] = useState(null);
  const [pnlBreakdown, setPnlBreakdown] = useState(null);
  const [selectedBotId, setSelectedBotId] = useState(null);
  const [fleetStats, setFleetStats] = useState(null);

  const fetchBotStatus = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        setBotStatus(data);
        setLastUpdate(Date.now());
        setPulse(true);
        setTimeout(() => setPulse(false), 500);
        if (data.current_price && prevPrice.current) {
          setPriceDir(data.current_price > prevPrice.current ? 1 : data.current_price < prevPrice.current ? -1 : 0);
        }
        prevPrice.current = data.current_price || 0;
        if (data.current_price) {
          setPriceHistory(h => [...h, { t: Date.now(), p: data.current_price }].slice(-60));
        }
      }
    } catch {}
  }, [token]);

  const fetchEquityHistory = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/equity-history`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        if (data.history) {
          // Group by bot_id and pick the one with most profit
          const byBot = {};
          for (const h of data.history) {
            const bid = h.bot_id || "unknown";
            if (!byBot[bid]) byBot[bid] = [];
            byBot[bid].push(h);
          }
          // Find bot with highest last PnL
          let bestBot = null;
          let bestPnl = -Infinity;
          for (const [bid, entries] of Object.entries(byBot)) {
            const lastPnl = entries[entries.length - 1]?.pnl || 0;
            if (lastPnl > bestPnl) {
              bestPnl = lastPnl;
              bestBot = bid;
            }
          }
          // Use the best bot's data for the chart, fallback to all
          const chartData = bestBot ? byBot[bestBot] : data.history;
          setEquityHistory(chartData.map(h => ({
            ...h,
            time: new Date(h.t).toLocaleString("el-GR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }),
            shortTime: new Date(h.t).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" }),
          })));
        }
      }
    } catch {}
  }, [token]);


  const fetchLogs = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/logs?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        setActivityLogs(data.logs || []);
      }
    } catch {}
  }, [token]);

  const fetchDealInsight = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/deal-insight`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setDealInsight(await r.json());
    } catch {}
  }, [token]);

  const fetchAllBots = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/all-bots`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        // Backend now partitions live vs shadow so dashboard aggregates stay clean.
        setAllBots(data.bots || []);
        setShadowBots(data.shadow_bots || []);
      }
    } catch {}
  }, [token]);

  const fetchFleetStats = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/fleet-stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setFleetStats(await r.json());
    } catch {}
  }, [token]);

  const fetchBotStatusById = useCallback(async (botId) => {
    if (!token || !botId) return;
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/status/${botId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        setBotStatus(data);
        setLastUpdate(Date.now());
      }
    } catch {}
  }, [token]);

  const fetchPortfolio = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/eu-bot/portfolio`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setPortfolio(await r.json());
    } catch {}
  }, [token]);

  const fetchLifetimeStats = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/lifetime-stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setLifetimeStats(await r.json());
    } catch {}
  }, [token]);

  const fetchPnlBreakdown = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/pnl-breakdown`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setPnlBreakdown(await r.json());
    } catch {}
  }, [token]);

  // Auto-select first bot when allBots loads
  useEffect(() => {
    if (allBots.length > 0 && !selectedBotId) {
      setSelectedBotId(allBots[0].bot_id);
    }
  }, [allBots, selectedBotId]);

  useEffect(() => {
    if (selectedBotId) {
      fetchBotStatusById(selectedBotId);
    }
  }, [selectedBotId, fetchBotStatusById]);

  useEffect(() => {
    fetchBotStatus();
    fetchEquityHistory();
    fetchLogs();
    fetchDealInsight();
    fetchAllBots();
    fetchPortfolio();
    fetchLifetimeStats();
    fetchPnlBreakdown();
    fetchFleetStats();
    const iv = setInterval(() => {
      if (selectedBotId) fetchBotStatusById(selectedBotId);
      else fetchBotStatus();
    }, 5000);
    const iv2 = setInterval(fetchEquityHistory, 60000);
    const iv3 = setInterval(fetchLogs, 10000);
    const iv4 = setInterval(fetchDealInsight, 8000);
    const iv5 = setInterval(fetchAllBots, 5000);
    const iv6 = setInterval(fetchPortfolio, 30000);
    const iv7 = setInterval(fetchLifetimeStats, 60000);
    const iv8 = setInterval(fetchFleetStats, 30000);
    return () => { clearInterval(iv); clearInterval(iv2); clearInterval(iv3); clearInterval(iv4); clearInterval(iv5); clearInterval(iv6); clearInterval(iv7); clearInterval(iv8); };
  }, [fetchBotStatus, fetchEquityHistory, fetchLogs, fetchDealInsight, fetchAllBots, fetchPortfolio, fetchLifetimeStats, fetchFleetStats, fetchBotStatusById, selectedBotId]);

  // Live seconds counter
  const [secAgo, setSecAgo] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => {
      if (lastUpdate) setSecAgo(Math.round((Date.now() - lastUpdate) / 1000));
    }, 1000);
    return () => clearInterval(iv);
  }, [lastUpdate]);

  const sendCommand = async (action) => {
    if (!token) return;
    setCmdLoading(true);
    try {
      await fetch(`${API}/api/eu-bot/dca/command`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action, params: {} }),
      });
      setTimeout(fetchBotStatus, 1000);
    } catch {}
    setCmdLoading(false);
  };

  const sendCommandToBot = async (botId, action) => {
    if (!token) return;
    setCmdLoading(true);
    try {
      await fetch(`${API}/api/eu-bot/dca/command/${botId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action, params: {} }),
      });
      setTimeout(fetchAllBots, 1000);
    } catch {}
    setCmdLoading(false);
  };

  const simulateInjection = async (amount) => {
    if (!token || !amount || amount <= 0) return;
    setInjectLoading(true);
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/simulate-injection`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount: parseFloat(amount) }),
      });
      if (r.ok) setInjectResult(await r.json());
    } catch {}
    setInjectLoading(false);
  };

  const executeManualBuy = async (amount) => {
    if (!token || !amount || amount <= 0) return;
    if (!window.confirm(`EXECUTE REAL BUY: Inject $${parseFloat(amount).toFixed(2)} into active deal?\n\nThis will use REAL MONEY.`)) return;
    setCmdLoading(true);
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/command`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "manual_buy", params: { amount: parseFloat(amount) } }),
      });
      if (r.ok) {
        setCmdResult("MANUAL BOOST queued - will execute on next heartbeat. Status will show 'MANUAL BOOSTED'");
        setInjectResult(null);
      } else {
        setCmdResult("Failed to queue manual_buy command");
      }
    } catch {
      setCmdResult("Error sending manual_buy command");
    }
    setCmdLoading(false);
  };

  const isConnected = botStatus?.connected;

  // Mini sparkline from price history
  const Sparkline = ({ data, width = 200, height = 40 }) => {
    if (!data || data.length < 2) return null;
    const prices = data.map(d => d.p);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const range = max - min || 1;
    const points = prices.map((p, i) => {
      const x = (i / (prices.length - 1)) * width;
      const y = height - ((p - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    }).join(" ");
    const isUp = prices[prices.length - 1] >= prices[0];
    return (
      <svg width={width} height={height} className="opacity-60">
        <polyline points={points} fill="none" stroke={isUp ? "#14b8a6" : "#ef4444"} strokeWidth="1.5" />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-zinc-100 p-4 md:p-6" data-testid="dca-dashboard">
      {/* CSS for flash animations + Fleet Commander HUD */}
      <style>{`
        .flash-green { color: #34d399 !important; text-shadow: 0 0 8px rgba(52,211,153,0.5); }
        .flash-red { color: #f87171 !important; text-shadow: 0 0 8px rgba(248,113,113,0.5); }
        @keyframes livePulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        .live-dot { animation: livePulse 1.5s ease-in-out infinite; }
        @keyframes slideIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        .slide-in { animation: slideIn 0.3s ease-out; }
        @keyframes progressGlow { 0%,100% { box-shadow: 0 0 4px rgba(20,184,166,0.3); } 50% { box-shadow: 0 0 12px rgba(20,184,166,0.6); } }
        .progress-glow { animation: progressGlow 2s ease-in-out infinite; }
        @keyframes heartbeat { 0%,100% { transform:scale(1); } 50% { transform:scale(1.15); } }
        .heartbeat { animation: heartbeat 0.5s ease-in-out; }
        .glow-text-green { text-shadow: 0 0 20px rgba(0,255,157,0.4), 0 0 40px rgba(0,255,157,0.1); }
        .glow-text-red { text-shadow: 0 0 20px rgba(255,0,85,0.4), 0 0 40px rgba(255,0,85,0.1); }
        .glow-text-cyan { text-shadow: 0 0 20px rgba(0,212,255,0.3); }
        .glass-card { backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); background: rgba(24,24,27,0.6); border: 1px solid rgba(255,255,255,0.08); }
        .glass-hero { backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); background: rgba(9,9,11,0.75); border: 1px solid rgba(255,255,255,0.1); }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes pulseGlow { 0%,100% { box-shadow: 0 0 8px rgba(0,255,157,0.2); } 50% { box-shadow: 0 0 20px rgba(0,255,157,0.5); } }
        .pulse-glow { animation: pulseGlow 2s ease-in-out infinite; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        .fade-up { animation: fadeUp 0.5s ease-out forwards; }
        .fade-up-d1 { animation-delay: 0.1s; opacity: 0; }
        .fade-up-d2 { animation-delay: 0.2s; opacity: 0; }
        @keyframes barShimmer { 0% { transform:translateX(-100%); } 100% { transform:translateX(200%); } }
        @keyframes neonPulse { 0%,100% { opacity:0.6; filter:blur(4px); } 50% { opacity:1; filter:blur(8px); } }
        @keyframes tpFlash { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
        @keyframes soFillPop { 0% { transform:scale(0.5); opacity:0; } 60% { transform:scale(1.3); } 100% { transform:scale(1); opacity:1; } }
        @keyframes profitCountUp { 0% { transform:translateY(8px); opacity:0; } 100% { transform:translateY(0); opacity:1; } }
        @keyframes cashGlow { 0%,100% { text-shadow: 0 0 10px rgba(0,255,157,0.3), 0 0 30px rgba(0,255,157,0.1); } 50% { text-shadow: 0 0 20px rgba(0,255,157,0.6), 0 0 50px rgba(0,255,157,0.2), 0 0 80px rgba(0,255,157,0.1); } }
        @keyframes dollarPulse { 0%,100% { transform:scale(1); } 50% { transform:scale(1.08); } }
        @keyframes borderGlow { 0%,100% { border-color: rgba(0,255,157,0.15); } 50% { border-color: rgba(0,255,157,0.4); } }
        .tp-close-flash { animation: tpFlash 0.8s ease-in-out infinite; }
        .so-pip-pop { animation: soFillPop 0.4s ease-out forwards; }
        .profit-glow { animation: cashGlow 3s ease-in-out infinite; }
        .profit-pop { animation: profitCountUp 0.6s ease-out forwards; }
        .dollar-pulse { animation: dollarPulse 2s ease-in-out infinite; }
        .card-border-glow { animation: borderGlow 3s ease-in-out infinite; }
      `}</style>

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-mono font-bold tracking-tight">
              SKYNET <span className="text-teal-400">DCA</span>
            </h1>
            <p className="text-[11px] text-zinc-600 font-mono mt-0.5">Natural Compound | DCA Bot</p>
          </div>
          <div className="flex items-center gap-4">
            {/* Live price ticker */}
            {botStatus?.current_price > 0 && (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all duration-300 ${
                priceDir > 0 ? "border-emerald-800/40 bg-emerald-900/10" : priceDir < 0 ? "border-red-800/40 bg-red-900/10" : "border-zinc-800 bg-zinc-900/50"
              }`} data-testid="live-price-ticker">
                {priceDir > 0 ? <TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> : priceDir < 0 ? <TrendingDown className="w-3.5 h-3.5 text-red-400" /> : null}
                <span className="text-xs text-zinc-500 font-mono">{botStatus.symbol?.replace("USDC","").replace("USDT","") || "?"}</span>
                <AnimNum value={botStatus.current_price} prefix="$" decimals={2}
                  color={priceDir > 0 ? "text-emerald-400" : priceDir < 0 ? "text-red-400" : "text-zinc-200"}
                  testId="price-ticker-value" />
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${isConnected ? "bg-emerald-400 live-dot" : "bg-zinc-600"}`} />
              <span className="text-xs font-mono text-zinc-400" data-testid="bot-connection-status">
                {isConnected ? (botStatus?.dry_run ? "DRY RUN" : "LIVE") : "OFFLINE"}
              </span>
              {isConnected && <span className="text-[10px] font-mono text-zinc-600">{secAgo}s</span>}
            </div>
          </div>
        </div>

        {/* ── GLOBAL HEADER STRIP (probation + shadow) ──
            Visible above tabs on every page so issues aren't hidden
            behind a tab the user doesn't happen to be looking at. */}
        {(fleetStats?.probation?.active || shadowBots.length > 0) && (
          <div className="space-y-2 mb-4" data-testid="global-header-strip">
            {fleetStats?.probation?.active && (
              <div
                data-testid="global-probation-banner"
                className={`rounded-lg border px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-[11px] font-mono ${
                  fleetStats.probation.should_pause
                    ? "bg-red-950/40 border-red-700/50 text-red-300"
                    : "bg-amber-950/30 border-amber-700/40 text-amber-300"
                }`}
              >
                <div>
                  <span className="font-bold uppercase tracking-wider mr-2">
                    {fleetStats.probation.should_pause ? "PROBATION → PAUSE" : "PROBATION WATCH"}
                  </span>
                  <span className="text-zinc-400">
                    {fleetStats.probation.bot_id}: {fleetStats.probation.deals_since}/{fleetStats.probation.min_deals} deals,
                    avg {fleetStats.probation.avg_profit_pct?.toFixed(2)}% (need ≥{fleetStats.probation.min_avg_profit_pct}%)
                  </span>
                </div>
                {fleetStats.probation.should_pause && (
                  <span className="text-red-400 font-bold">Pause command is flowing on next heartbeat.</span>
                )}
              </div>
            )}
            {shadowBots.length > 0 && (() => {
              const shadowEquity = shadowBots.reduce((s, b) => s + (b.equity || 0), 0);
              const shadowCapital = shadowBots.reduce((s, b) => s + (b.initial_capital || 0), 0);
              const shadowPnl = shadowBots.reduce((s, b) => s + (b.total_profit || 0), 0);
              const shadowDeals = shadowBots.reduce((s, b) => s + (b.deals_completed || 0), 0);
              const onlineShadow = shadowBots.filter(b => b.connected).length;
              return (
                <div
                  data-testid="global-shadow-strip"
                  className="rounded-lg border border-purple-800/40 bg-purple-950/20 px-3 py-2 flex flex-wrap items-center gap-3 text-[11px] font-mono text-purple-200"
                >
                  <span className="px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/40 text-[10px] font-bold uppercase tracking-wider text-purple-300">
                    SHADOW
                  </span>
                  <span className="text-zinc-400">
                    {onlineShadow}/{shadowBots.length} online · paper capital ${shadowCapital.toFixed(0)} · equity ${shadowEquity.toFixed(0)}
                  </span>
                  <span className={shadowPnl >= 0 ? "text-emerald-400" : "text-red-400"}>
                    {shadowPnl >= 0 ? "+" : ""}${shadowPnl.toFixed(2)}
                  </span>
                  <span className="text-zinc-500">· {shadowDeals} deals</span>
                  <span className="ml-auto text-[10px] text-purple-400/70">
                    Not counted in live totals
                  </span>
                </div>
              );
            })()}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-zinc-800 overflow-x-auto scrollbar-hide" data-testid="dca-tabs">
          {[
            // Trimmed tab set — 2026-04-16. Removed: "dashboard" (legacy single-bot,
            // redundant with Fleet), "projections" (aspirational $2k→$100k table,
            // now folded into Fleet Command's EST/DAY/EST/MONTH), "analytics"
            // (duplicated Performance heatmaps), "alerts" (profit-target config,
            // set-and-forget — route via Settings when needed).
            { id: "magazi", label: "Magazi", icon: <Wallet className="w-3.5 h-3.5" /> },
            { id: "arena", label: "Arena", icon: <Trophy className="w-3.5 h-3.5" /> },
            { id: "etfs", label: "ETFs", icon: <BarChart3 className="w-3.5 h-3.5" /> },
            { id: "portfolios", label: "Portfolios", icon: <Brain className="w-3.5 h-3.5" /> },
            { id: "deals", label: "Deals", icon: <Target className="w-3.5 h-3.5" /> },
            { id: "labengine", label: "Lab Engine", icon: <Database className="w-3.5 h-3.5" /> },
            { id: "fleet", label: "Crypto Fleet", icon: <Shield className="w-3.5 h-3.5" /> },
            { id: "logs", label: "Logs", icon: <Terminal className="w-3.5 h-3.5" /> },
          ].map(t => (
            <button key={t.id} data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 md:px-4 py-2 text-xs md:text-sm font-mono transition-all duration-200 rounded-t whitespace-nowrap ${
                tab === t.id ? "bg-zinc-800 text-teal-400 border border-zinc-700 border-b-0" : "text-zinc-500 hover:text-zinc-300"
              }`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* DASHBOARD */}
        {/* LEGACY DASHBOARD TAB removed 2026-04-16. Was a ~1000-line
            single-bot view from pre-multi-bot days; Fleet tab now covers
            the same ground for all live + shadow bots with cleaner numbers.
            Code preserved in git history if we ever need to resurrect a piece. */}

        {/* LOGS TAB */}
        {tab === "logs" && (
          <div className="space-y-3 slide-in" data-testid="logs-tab">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm font-mono font-bold text-zinc-300 flex items-center gap-2">
                <Terminal className="w-4 h-4" /> Bot Activity Log
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-zinc-600">{activityLogs.length} entries</span>
                <button onClick={fetchLogs} data-testid="refresh-logs-btn"
                  className="px-3 py-1.5 text-[10px] font-mono text-zinc-400 border border-zinc-700 rounded hover:bg-zinc-800 transition-all">
                  <RefreshCw className="w-3 h-3 inline mr-1" /> Refresh
                </button>
              </div>
            </div>

            <div className="bg-zinc-950/80 border border-zinc-800 rounded-lg overflow-hidden">
              {activityLogs.length === 0 ? (
                <div className="text-center py-12 text-zinc-600 text-xs font-mono">
                  No activity yet. Logs will appear as the bot sends heartbeats.
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto divide-y divide-zinc-800/30">
                  {activityLogs.map((log, i) => {
                    const typeColors = {
                      heartbeat: "text-teal-400 bg-teal-900/20 border-teal-800/30",
                      deal_open: "text-amber-400 bg-amber-900/20 border-amber-800/30",
                      deal_close: "text-emerald-400 bg-emerald-900/20 border-emerald-800/30",
                      so_fill: "text-orange-400 bg-orange-900/20 border-orange-800/30",
                      pause: "text-red-400 bg-red-900/20 border-red-800/30",
                      resume: "text-blue-400 bg-blue-900/20 border-blue-800/30",
                      connect: "text-purple-400 bg-purple-900/20 border-purple-800/30",
                      price_move: "text-zinc-400 bg-zinc-900/20 border-zinc-800/30",
                    };
                    const colors = typeColors[log.type] || "text-zinc-400 bg-zinc-900/20 border-zinc-800/30";
                    const time = log.t ? new Date(log.t).toLocaleString("el-GR", {
                      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit"
                    }) : "";

                    return (
                      <div key={i} className="flex flex-col sm:flex-row items-start gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 hover:bg-zinc-900/40 transition-all" data-testid={`log-entry-${i}`}>
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                          <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded border shrink-0 ${colors}`}>
                            {log.type?.toUpperCase().replace("_", " ")}
                          </span>
                          <span className="text-[10px] font-mono text-zinc-600 sm:hidden">{time}</span>
                        </div>
                        <div className="flex-1 min-w-0 w-full">
                          <div className="text-xs font-mono text-zinc-300 break-words leading-relaxed">{log.msg}</div>
                          {log.data?.equity && (
                            <div className="text-[10px] font-mono text-zinc-600 mt-0.5">
                              Equity: ${log.data.equity?.toFixed(2)} | Deals: {log.data.deals}
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] font-mono text-zinc-600 shrink-0 hidden sm:block">{time}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}



        {/* FLEET — Crypto Fleet Commander HUD */}
        {tab === "fleet" && (
          <div className="space-y-6" data-testid="fleet-tab">
            {allBots.length === 0 && shadowBots.length === 0 ? (
              <div className="text-center py-20 glass-card rounded-3xl" data-testid="fleet-empty">
                <Shield className="w-14 h-14 mx-auto mb-4 text-zinc-700" />
                <div className="text-base font-bold text-zinc-400" style={{fontFamily:'Manrope,sans-serif'}}>No bots connected</div>
                <div className="text-xs text-zinc-600 mt-2" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Deploy a bot to your server to see it here</div>
                <button onClick={() => setTab("deploy")} className="mt-6 px-6 py-2.5 text-xs font-mono text-[#00d4ff] border border-[#00d4ff]/30 rounded-full hover:bg-[#00d4ff]/10 transition-all">
                  Deploy Instructions
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Fleet Summary */}
                {allBots.length > 0 && (() => {
                  const totalEquity = allBots.reduce((s, b) => s + (b.equity || 0), 0);
                  const totalCapital = allBots.reduce((s, b) => s + (b.initial_capital || 0), 0);
                  // SESSION realized PnL from each bot's current heartbeat —
                  // resets to 0 whenever a bot restarts.
                  const sessionRealizedPnl = allBots.reduce((s, b) => s + (b.total_profit || 0), 0);
                  // LIFETIME realized PnL — the authoritative number the user
                  // actually wants to see. Aggregated by the backend from the
                  // full dca_deal_history collection (live-only after Commit 3).
                  // Fall back to session only if fleet-stats hasn't loaded yet.
                  const totalRealizedPnl = fleetStats?.fleet?.total_profit ?? sessionRealizedPnl;
                  const totalUnrealizedPnl = allBots.reduce((s, b) => {
                    if (b.in_deal && b.avg_entry_price > 0) {
                      return s + ((b.current_price || 0) - b.avg_entry_price) * (b.total_qty || 0);
                    }
                    return s;
                  }, 0);
                  const totalPnl = totalRealizedPnl + totalUnrealizedPnl;
                  const totalDeals = allBots.reduce((s, b) => s + (b.deals_completed || 0), 0);
                  const onlineBots = allBots.filter(b => b.connected).length;
                  // EST/DAY and EST/MONTH now use the REAL trailing fleet APY
                  // (capital-weighted, computed backend-side from actual deal
                  // history over the live lifetime of each bot). Falls back
                  // to 51% backtest APY only if the backend hasn't produced
                  // an actual APY yet (e.g. brand-new fleet with no deals).
                  const fleetApyPct = (fleetStats?.fleet?.actual_apy ?? 51) / 100;
                  const estDailyTotal = allBots.reduce((s, b) => {
                    const eq = b.equity || b.initial_capital || 0;
                    return s + (eq * fleetApyPct / 365);
                  }, 0);
                  const estMonthlyTotal = allBots.reduce((s, b) => {
                    const eq = b.equity || b.initial_capital || 0;
                    return s + (eq * fleetApyPct / 12);
                  }, 0);
                  return (
                    <>
                      {/* ═══ MOBILE: Fleet Commander HUD ═══ */}
                      <div className="md:hidden" data-testid="fleet-summary-mobile">
                        {/* Hero Section with background image */}
                        <div className="relative overflow-hidden rounded-3xl mb-5 fade-up" style={{minHeight: '200px'}}>
                          {/* Background image */}
                          <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: `url('https://images.unsplash.com/photo-1762278804941-27ff5cba5a2e?w=800&q=60')`, filter: 'brightness(0.15) saturate(0.6)'}} />
                          {/* Glass overlay */}
                          <div className="relative z-10 glass-hero rounded-3xl p-7" style={{background: 'rgba(9,9,11,0.72)'}}>
                            {/* FLEET COMMAND header */}
                            <div className="flex items-center justify-between mb-5">
                              <div className="flex items-center gap-2">
                                <div className={`w-2.5 h-2.5 rounded-full ${onlineBots > 0 ? 'live-dot' : ''}`} style={{backgroundColor: onlineBots > 0 ? '#00ff9d' : '#52525b'}} />
                                <span className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 font-semibold" style={{fontFamily:'Manrope,sans-serif'}}>Fleet Command</span>
                              </div>
                              <span className="text-[10px] font-mono text-zinc-600">{onlineBots}/{allBots.length} online</span>
                            </div>
                            {/* Total Equity — the hero number */}
                            <div className="mb-2">
                              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Total Equity</div>
                              <div className="text-5xl font-extrabold tracking-tighter text-white glow-text-cyan" style={{fontFamily:"'JetBrains Mono',monospace"}}>
                                ${totalEquity.toFixed(0)}
                              </div>
                            </div>
                            {/* PnL Badge */}
                            <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full border" style={{background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)'}}>
                              {totalPnl >= 0 ? <TrendingUp className="w-3.5 h-3.5" style={{color:'#00ff9d'}} /> : <TrendingDown className="w-3.5 h-3.5" style={{color:'#ff0055'}} />}
                              <span className="text-lg font-bold font-mono" style={{color: totalPnl >= 0 ? '#00ff9d' : '#ff0055', textShadow: totalPnl >= 0 ? '0 0 12px rgba(0,255,157,0.3)' : '0 0 12px rgba(255,0,85,0.3)'}}>
                                {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)}
                              </span>
                              <span className="text-[10px] text-zinc-500 font-mono">USD</span>
                            </div>
                            {/* Quick stats row */}
                            <div className="grid grid-cols-4 gap-3 mt-5 pt-4 border-t border-white/5">
                              <div className="text-center">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Capital</div>
                                <div className="text-sm font-bold font-mono text-zinc-300">${totalCapital.toFixed(0)}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Est/Day</div>
                                <div className="text-sm font-bold font-mono" style={{color:'#00d4ff'}}>${estDailyTotal.toFixed(0)}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Est/Month</div>
                                <div className="text-sm font-bold font-mono" style={{color:'#00d4ff'}}>${estMonthlyTotal.toFixed(0)}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Deals</div>
                                <div className="text-sm font-bold font-mono text-zinc-300">{totalDeals}</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Bot Cards: Horizontal Snap Slider */}
                        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 no-scrollbar fade-up fade-up-d1" style={{WebkitOverflowScrolling: 'touch'}}>
                          {allBots.map(bot => {
                            const e = bot.avg_entry_price || 0;
                            const c = bot.current_price || 0;
                            const q = bot.total_qty || 0;
                            const tp = bot.take_profit_pct || 1;
                            const tpPrice = e > 0 ? e * (1 + tp / 100) : 0;
                            const pctPnl = e > 0 ? ((c - e) / e * 100) : 0;
                            const prog = Math.max(0, Math.min((pctPnl / tp) * 100, 100));
                            const dealProfit = e > 0 && q > 0 ? (tpPrice - e) * q : 0;
                            const eq = bot.equity || bot.initial_capital || 0;
                            const apy = 0.51;
                            const estDaily = eq * apy / 365;
                            const estMonthly = eq * apy / 12;
                            const pnlColor = pctPnl >= 0 ? '#00ff9d' : '#ff0055';
                            const coinSymbol = bot.symbol?.replace('USDC','').replace('USDT','') || '?';
                            const soFilled = bot.safety_orders_filled || 0;
                            const maxSo = bot.max_safety_orders || 15;
                            // Circular gauge
                            const r = 34;
                            const circ = 2 * Math.PI * r;
                            const offset = circ - (prog / 100) * circ;
                            const gaugeStroke = prog >= 60 ? '#00ff9d' : pctPnl < 0 ? '#ff0055' : '#00d4ff';
                            return (
                              <div key={bot.bot_id} data-testid={`fleet-mobile-card-${bot.bot_id}`}
                                className="min-w-[85vw] max-w-[330px] snap-center flex-shrink-0 rounded-3xl p-6 flex flex-col justify-between transition-all duration-300"
                                style={{background: 'linear-gradient(160deg, #18181b 0%, #0c0c0f 100%)', border: `1px solid ${bot.connected ? 'rgba(255,255,255,0.08)' : 'rgba(82,82,91,0.3)'}`, minHeight: '340px', boxShadow: bot.in_deal ? `0 0 30px -8px ${gaugeStroke}20` : 'none'}}>
                                {/* Card Header */}
                                <div>
                                  <div className="flex justify-between items-center mb-5">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black border-2 transition-all" style={{borderColor: bot.connected ? '#00ff9d' : '#52525b', color: bot.connected ? '#fafafa' : '#52525b', background: bot.connected ? 'rgba(0,255,157,0.08)' : 'transparent'}}>
                                        {coinSymbol}
                                      </div>
                                      <div>
                                        <div className="text-base font-extrabold text-white uppercase tracking-wide" style={{fontFamily:'Manrope,sans-serif'}}>{bot.symbol?.replace('USDC','/USDC') || '?'}</div>
                                        <div className="text-[9px] text-zinc-500 uppercase tracking-wider" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>{bot.bot_id?.replace('-',' ')}</div>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <div className={`w-2 h-2 rounded-full ${bot.connected ? "live-dot" : ""}`} style={{backgroundColor: bot.connected ? '#00ff9d' : '#52525b'}} />
                                      <span className="text-[10px] font-mono" style={{color: bot.status === 'IN_DEAL' ? '#ffbe0b' : bot.status === 'PAUSED' ? '#ff0055' : '#00ff9d'}}>{bot.status}</span>
                                    </div>
                                  </div>

                                  {/* PnL + Circular Gauge row */}
                                  <div className="flex items-center justify-between mb-5">
                                    <div>
                                      <div className="text-4xl font-extrabold font-mono" style={{color: pnlColor, textShadow: `0 0 24px ${pnlColor}30`}}>
                                        {pctPnl >= 0 ? "+" : ""}{pctPnl.toFixed(2)}%
                                      </div>
                                      <div className="text-[10px] text-zinc-600 font-mono mt-1">
                                        ${eq.toFixed(0)} equity
                                      </div>
                                    </div>
                                    {/* Circular Progress Gauge */}
                                    <div className="relative" style={{width: 80, height: 80}}>
                                      <svg width="80" height="80" className="transform -rotate-90">
                                        <circle cx="40" cy="40" r={r} fill="none" stroke="#27272a" strokeWidth="5" />
                                        <circle cx="40" cy="40" r={r} fill="none" stroke={gaugeStroke} strokeWidth="5"
                                          strokeDasharray={circ} strokeDashoffset={offset}
                                          strokeLinecap="round" className="transition-all duration-1000"
                                          style={{filter: `drop-shadow(0 0 4px ${gaugeStroke}60)`}} />
                                      </svg>
                                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        <span className="text-sm font-bold font-mono text-white">{prog.toFixed(0)}%</span>
                                        <span className="text-[8px] text-zinc-600">to TP</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Data Grid 2x2 */}
                                <div className="grid grid-cols-2 gap-2.5 mb-4">
                                  <div className="bg-white/[0.03] rounded-xl p-3">
                                    <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>TP Target</div>
                                    <div className="text-base font-bold font-mono" style={{color:'#00ff9d'}}>${tpPrice < 1 ? tpPrice.toFixed(4) : tpPrice.toFixed(2)}</div>
                                  </div>
                                  <div className="bg-white/[0.03] rounded-xl p-3">
                                    <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Deal Profit</div>
                                    <div className="text-base font-bold font-mono" style={{color:'#ffbe0b'}}>${dealProfit.toFixed(2)}</div>
                                  </div>
                                  <div className="bg-white/[0.03] rounded-xl p-3">
                                    <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Est/Day</div>
                                    <div className="text-base font-bold font-mono" style={{color:'#00d4ff'}}>${estDaily.toFixed(1)}</div>
                                  </div>
                                  <div className="bg-white/[0.03] rounded-xl p-3">
                                    <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Est/Month</div>
                                    <div className="text-base font-bold font-mono" style={{color:'#00d4ff'}}>${estMonthly.toFixed(0)}</div>
                                  </div>
                                </div>

                                {/* Footer: SO + controls */}
                                <div className="flex justify-between items-center pt-3 border-t border-white/5">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-zinc-500 uppercase" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>SO</span>
                                    <div className="flex gap-0.5">
                                      {Array.from({length: Math.min(maxSo, 15)}).map((_, i) => (
                                        <div key={i} className="w-1.5 h-3 rounded-sm transition-all" style={{backgroundColor: i < soFilled ? '#ffbe0b' : '#27272a', boxShadow: i < soFilled ? '0 0 4px rgba(255,190,11,0.3)' : 'none'}} />
                                      ))}
                                    </div>
                                    <span className="text-[10px] font-mono text-zinc-500">{soFilled}/{maxSo}</span>
                                  </div>
                                  {bot.status === "PAUSED" ? (
                                    <button onClick={() => sendCommandToBot(bot.bot_id, "resume")} disabled={cmdLoading}
                                      className="px-3 py-1.5 text-[10px] font-mono rounded-full border transition-all disabled:opacity-50"
                                      style={{color:'#00ff9d', borderColor:'rgba(0,255,157,0.3)', background:'rgba(0,255,157,0.08)'}}>
                                      <Play className="w-3 h-3 inline mr-1" />Resume
                                    </button>
                                  ) : (
                                    <button onClick={() => sendCommandToBot(bot.bot_id, "pause")} disabled={cmdLoading}
                                      className="px-3 py-1.5 text-[10px] font-mono text-zinc-400 rounded-full border border-zinc-700 bg-zinc-800/50 hover:bg-zinc-700/50 transition-all disabled:opacity-50">
                                      <Pause className="w-3 h-3 inline mr-1" />Pause
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Ghost bots cleanup */}
                        {allBots.some(b => !b.connected) && (
                          <button data-testid="clear-fleet-btn-mobile"
                            onClick={async () => { try { await fetch(`${API}/api/eu-bot/dca/clear-fleet`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }); } catch {} }}
                            className="w-full mt-2 py-2 text-[10px] font-mono text-red-400/60 border border-red-900/20 rounded-xl hover:bg-red-900/10 transition-all">
                            Clear disconnected bots
                          </button>
                        )}
                      </div>

                      {/* ═══ DESKTOP: Fleet Commander HUD ═══ */}
                      <div className="hidden md:block" data-testid="fleet-summary-desktop">
                        {/* Hero Bar */}
                        <div className="relative overflow-hidden rounded-2xl mb-6">
                          <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: `url('https://images.unsplash.com/photo-1762278804941-27ff5cba5a2e?w=1200&q=60')`, filter: 'brightness(0.12) saturate(0.5)'}} />
                          <div className="relative z-10 glass-hero rounded-2xl p-8">
                            <div className="flex items-end justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <div className={`w-2.5 h-2.5 rounded-full ${onlineBots > 0 ? 'live-dot' : ''}`} style={{backgroundColor: onlineBots > 0 ? '#00ff9d' : '#52525b'}} />
                                  <span className="text-xs uppercase tracking-[0.25em] text-zinc-400 font-semibold" style={{fontFamily:'Manrope,sans-serif'}}>Fleet Command</span>
                                  <span className="text-[10px] font-mono text-zinc-600">{onlineBots}/{allBots.length} online</span>
                                </div>
                                <div className="text-5xl font-extrabold tracking-tighter text-white glow-text-cyan" style={{fontFamily:"'JetBrains Mono',monospace"}}>
                                  ${totalEquity.toFixed(0)}
                                </div>
                                <div className="inline-flex items-center gap-2 mt-3 px-4 py-1.5 rounded-full" style={{background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)'}}>
                                  {totalPnl >= 0 ? <TrendingUp className="w-3.5 h-3.5" style={{color:'#00ff9d'}} /> : <TrendingDown className="w-3.5 h-3.5" style={{color:'#ff0055'}} />}
                                  <span className="text-lg font-bold font-mono" style={{color: totalPnl >= 0 ? '#00ff9d' : '#ff0055'}}>
                                    {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)}
                                  </span>
                                  <span className="text-[10px] text-zinc-500 font-mono">
                                    (R: ${totalRealizedPnl.toFixed(2)} | F: ${totalUnrealizedPnl.toFixed(2)})
                                  </span>
                                </div>
                              </div>
                              <div className="grid grid-cols-4 gap-6">
                                <div className="text-center">
                                  <div className="text-[9px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Capital</div>
                                  <div className="text-lg font-bold font-mono text-zinc-300">${totalCapital.toFixed(0)}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-[9px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Est/Day</div>
                                  <div className="text-lg font-bold font-mono" style={{color:'#00d4ff'}}>${estDailyTotal.toFixed(0)}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-[9px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Est/Month</div>
                                  <div className="text-lg font-bold font-mono" style={{color:'#00d4ff'}}>${estMonthlyTotal.toFixed(0)}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-[9px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Deals</div>
                                  <div className="text-lg font-bold font-mono text-zinc-300">{totalDeals}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}

                {/* Desktop Individual Bot Cards */}
                <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {allBots.map(bot => {
                    const isOnline = bot.connected;
                    const pnl = bot.total_profit || 0;
                    const eq = bot.equity || 0;
                    const cap = bot.initial_capital || 0;
                    const entry = bot.avg_entry_price || 0;
                    const curr = bot.current_price || 0;
                    const qty = bot.total_qty || 0;
                    const unrealizedPnl = bot.in_deal && entry > 0 ? (curr - entry) * qty : 0;
                    const totalPnlLive = pnl + unrealizedPnl;
                    const tp = bot.take_profit_pct || 1;
                    const tpPrice = entry > 0 ? entry * (1 + tp / 100) : 0;
                    const pctPnl = entry > 0 ? ((curr - entry) / entry * 100) : 0;
                    const prog = Math.max(0, Math.min((pctPnl / tp) * 100, 100));
                    const dealProfit = entry > 0 && qty > 0 ? (tpPrice - entry) * qty : 0;
                    const apy = 0.51;
                    const estDaily = eq * apy / 365;
                    const estMonthly = eq * apy / 12;
                    const coinSymbol = bot.symbol?.replace('USDC','').replace('USDT','') || '?';
                    const soFilled = bot.safety_orders_filled || 0;
                    const maxSo = bot.max_safety_orders || 15;
                    const pnlColor = pctPnl >= 0 ? '#00ff9d' : '#ff0055';
                    const gaugeStroke = prog >= 60 ? '#00ff9d' : pctPnl < 0 ? '#ff0055' : '#00d4ff';
                    const r = 38;
                    const circ = 2 * Math.PI * r;
                    const offset = circ - (prog / 100) * circ;

                    return (
                      <div key={bot.bot_id} data-testid={`fleet-bot-${bot.bot_id}`}
                        className="rounded-2xl p-5 transition-all duration-300 hover:translate-y-[-2px]"
                        style={{background: 'linear-gradient(160deg, #18181b 0%, #0c0c0f 100%)', border: `1px solid ${isOnline ? 'rgba(255,255,255,0.08)' : 'rgba(82,82,91,0.3)'}`, boxShadow: bot.in_deal ? `0 4px 30px -8px ${gaugeStroke}15` : 'none'}}>
                        {/* Header */}
                        <div className="flex items-center justify-between mb-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black border-2" style={{borderColor: isOnline ? '#00ff9d' : '#52525b', color: isOnline ? '#fafafa' : '#52525b', background: isOnline ? 'rgba(0,255,157,0.08)' : 'transparent'}}>
                              {coinSymbol}
                            </div>
                            <div>
                              <div className="text-base font-extrabold text-white uppercase tracking-wide" style={{fontFamily:'Manrope,sans-serif'}}>{bot.symbol?.replace('USDC','/USDC') || '?'}</div>
                              <div className="text-[9px] text-zinc-500 uppercase tracking-wider" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>{bot.bot_id?.replace('-',' ')}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {bot.uptime_hours != null && (
                              <span className="text-[9px] font-mono text-zinc-600" title="Bot uptime">
                                ⏱ {bot.uptime_hours < 24 ? `${bot.uptime_hours}h` : `${(bot.uptime_hours/24).toFixed(1)}d`}
                              </span>
                            )}
                            <div className={`w-2 h-2 rounded-full ${isOnline ? "live-dot" : ""}`} style={{backgroundColor: isOnline ? '#00ff9d' : '#52525b'}} />
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border" style={{color: bot.status === 'IN_DEAL' ? '#ffbe0b' : bot.status === 'PAUSED' ? '#ff0055' : '#00ff9d', borderColor: bot.status === 'IN_DEAL' ? 'rgba(255,190,11,0.2)' : bot.status === 'PAUSED' ? 'rgba(255,0,85,0.2)' : 'rgba(0,255,157,0.2)', background: bot.status === 'IN_DEAL' ? 'rgba(255,190,11,0.06)' : bot.status === 'PAUSED' ? 'rgba(255,0,85,0.06)' : 'rgba(0,255,157,0.06)'}}>
                              {bot.status || "IDLE"}
                            </span>
                          </div>
                        </div>

                        {/* Main content: PnL + Gauge + Stats */}
                        <div className="flex items-start gap-6">
                          {/* Left: PnL + Equity */}
                          <div className="flex-1">
                            <div className="text-3xl font-extrabold font-mono mb-1" style={{color: pnlColor, textShadow: `0 0 20px ${pnlColor}25`}}>
                              {pctPnl >= 0 ? "+" : ""}{pctPnl.toFixed(2)}%
                            </div>
                            <div className="flex items-center gap-3 mb-4">
                              <span className="text-lg font-bold font-mono text-white">${eq.toFixed(0)}</span>
                              <span className="text-xs font-mono" style={{color: totalPnlLive >= 0 ? '#00ff9d' : '#ff0055'}}>
                                {totalPnlLive >= 0 ? "+" : ""}{totalPnlLive.toFixed(2)}
                              </span>
                            </div>
                            {/* Data Grid */}
                            <div className="grid grid-cols-2 gap-2">
                              <div className="bg-white/[0.03] rounded-lg p-2.5">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>TP Target</div>
                                <div className="text-sm font-bold font-mono" style={{color:'#00ff9d'}}>${tpPrice < 1 ? tpPrice.toFixed(4) : tpPrice.toFixed(2)}</div>
                              </div>
                              <div className="bg-white/[0.03] rounded-lg p-2.5">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Deal Profit</div>
                                <div className="text-sm font-bold font-mono" style={{color:'#ffbe0b'}}>${dealProfit.toFixed(2)}</div>
                              </div>
                              <div className="bg-white/[0.03] rounded-lg p-2.5">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Est/Day</div>
                                <div className="text-sm font-bold font-mono" style={{color:'#00d4ff'}}>${estDaily.toFixed(1)}</div>
                              </div>
                              <div className="bg-white/[0.03] rounded-lg p-2.5">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>Est/Month</div>
                                <div className="text-sm font-bold font-mono" style={{color:'#00d4ff'}}>${estMonthly.toFixed(0)}</div>
                              </div>
                            </div>
                          </div>
                          {/* Right: Circular Gauge */}
                          <div className="flex flex-col items-center">
                            <div className="relative" style={{width: 100, height: 100}}>
                              <svg width="100" height="100" className="transform -rotate-90">
                                <circle cx="50" cy="50" r={r} fill="none" stroke="#27272a" strokeWidth="5" />
                                <circle cx="50" cy="50" r={r} fill="none" stroke={gaugeStroke} strokeWidth="5"
                                  strokeDasharray={circ} strokeDashoffset={offset}
                                  strokeLinecap="round" className="transition-all duration-1000"
                                  style={{filter: `drop-shadow(0 0 5px ${gaugeStroke}60)`}} />
                              </svg>
                              <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-lg font-bold font-mono text-white">{prog.toFixed(0)}%</span>
                                <span className="text-[8px] text-zinc-500">to TP</span>
                              </div>
                            </div>
                            {/* SO indicators */}
                            <div className="flex gap-0.5 mt-2">
                              {Array.from({length: Math.min(maxSo, 15)}).map((_, i) => (
                                <div key={i} className="w-1.5 h-3 rounded-sm transition-all" style={{backgroundColor: i < soFilled ? '#ffbe0b' : '#27272a', boxShadow: i < soFilled ? '0 0 3px rgba(255,190,11,0.3)' : 'none'}} />
                              ))}
                            </div>
                            <span className="text-[9px] text-zinc-600 font-mono mt-1">SO {soFilled}/{maxSo}</span>
                          </div>
                        </div>

                        {/* Controls */}
                        <div className="flex gap-2 mt-4 pt-3 border-t border-white/5">
                          {bot.status === "PAUSED" ? (
                            <button onClick={() => sendCommandToBot(bot.bot_id, "resume")} disabled={cmdLoading}
                              data-testid={`fleet-resume-${bot.bot_id}`}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-mono rounded-lg transition-all disabled:opacity-50"
                              style={{color:'#00ff9d', borderColor:'rgba(0,255,157,0.3)', border:'1px solid rgba(0,255,157,0.3)', background:'rgba(0,255,157,0.08)'}}>
                              <Play className="w-3 h-3" /> Resume
                            </button>
                          ) : (
                            <button onClick={() => sendCommandToBot(bot.bot_id, "pause")} disabled={cmdLoading}
                              data-testid={`fleet-pause-${bot.bot_id}`}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-mono text-zinc-400 rounded-lg border border-zinc-700 bg-zinc-800/50 hover:bg-zinc-700/50 transition-all disabled:opacity-50">
                              <Pause className="w-3 h-3" /> Pause
                            </button>
                          )}
                          {allBots.some(b => !b.connected) && (
                            <button data-testid="clear-fleet-btn"
                              onClick={async () => { try { await fetch(`${API}/api/eu-bot/dca/clear-fleet`, { method: "POST", headers: { Authorization: `Bearer ${token}` } }); } catch {} }}
                              className="px-3 py-2 text-[10px] font-mono text-red-400/60 rounded-lg border border-red-900/20 hover:bg-red-900/10 transition-all">
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* ═══ SHADOW FLEET (paper trades, walled off from live totals) ═══ */}
                {shadowBots.length > 0 && (
                  <div className="mt-6" data-testid="shadow-fleet-section">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/40 text-[10px] font-bold uppercase tracking-wider text-purple-300">
                        SHADOW
                      </span>
                      <span className="text-xs font-mono text-zinc-400 uppercase tracking-widest">
                        Shadow Fleet — paper trades (not in live totals)
                      </span>
                      <span className="text-[10px] font-mono text-zinc-600">
                        {shadowBots.filter(b => b.connected).length}/{shadowBots.length} online
                      </span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {shadowBots.map(bot => {
                        const pnl = bot.total_profit || 0;
                        const eq = bot.equity || 0;
                        const cap = bot.initial_capital || 0;
                        const pctPnl = cap > 0 ? (pnl / cap * 100) : 0;
                        const coinSymbol = bot.symbol?.replace('USDC','').replace('USDT','') || '?';
                        const pnlColor = pnl >= 0 ? '#00ff9d' : '#ff0055';
                        const isOnline = bot.connected;
                        return (
                          <div key={bot.bot_id} data-testid={`shadow-fleet-bot-${bot.bot_id}`}
                            className="rounded-2xl p-4 transition-all duration-300"
                            style={{background: 'linear-gradient(160deg, #1a0f24 0%, #0c0712 100%)', border: `1px dashed ${isOnline ? 'rgba(168,85,247,0.35)' : 'rgba(82,82,91,0.3)'}`}}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-black border-2" style={{borderColor: isOnline ? '#a855f7' : '#52525b', color: isOnline ? '#fafafa' : '#52525b', background: isOnline ? 'rgba(168,85,247,0.10)' : 'transparent'}}>
                                  {coinSymbol}
                                </div>
                                <div>
                                  <div className="text-sm font-bold text-white uppercase tracking-wide">{bot.symbol || '?'}</div>
                                  <div className="text-[9px] text-purple-300/70 uppercase tracking-wider">{bot.bot_id}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${isOnline ? "live-dot" : ""}`} style={{backgroundColor: isOnline ? '#a855f7' : '#52525b'}} />
                                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-purple-500/30 text-purple-300 bg-purple-500/10">
                                  {bot.status || "IDLE"}
                                </span>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div className="bg-white/[0.03] rounded-lg p-2">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest">Paper Equity</div>
                                <div className="text-sm font-bold font-mono text-white">${eq.toFixed(0)}</div>
                              </div>
                              <div className="bg-white/[0.03] rounded-lg p-2">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest">Paper PnL</div>
                                <div className="text-sm font-bold font-mono" style={{color: pnlColor}}>
                                  {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}
                                </div>
                              </div>
                              <div className="bg-white/[0.03] rounded-lg p-2">
                                <div className="text-[8px] text-zinc-600 uppercase tracking-widest">Return %</div>
                                <div className="text-sm font-bold font-mono" style={{color: pnlColor}}>
                                  {pctPnl >= 0 ? "+" : ""}{pctPnl.toFixed(2)}%
                                </div>
                              </div>
                            </div>
                            <div className="mt-2 text-[9px] font-mono text-purple-400/60">
                              Capital ${cap.toFixed(0)} · {bot.deals_completed || 0} deals
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Binance Portfolio section removed 2026-04-16 — it showed
                    a different total than Fleet Command (e.g. $1,719 spot vs
                    $2,023 equity) for the same money, and the per-asset list
                    included dust like ZEC $2 / ETH $1 that polluted the view.
                    Full portfolio still accessible via the Portfolio tab. */}
              </div>
            )}
          </div>
        )}

        {/* PERFORMANCE TAB */}
        {tab === "performance" && <PerformanceTab token={token} />}

        {/* DEAL LOG TAB */}
        {tab === "deallog" && <DealLogTab token={token} />}

        {/* CODEX RISK TAB */}
        {tab === "codex" && <CodexRiskDashboard token={token} />}

        {/* AUTOPILOT TAB */}
        {tab === "autopilot" && <AutopilotTab token={token} />}

        {/* SKYNET BRAIN TAB (agent pipeline artifacts) */}
        {tab === "brain" && <SkynetBrainTab token={token} />}

        {/* STRATEGY LAB TAB */}
        {tab === "stratlab" && <StrategyLabTab />}

        {tab === "deals" && <DealsDashboard token={token} />}
        {tab === "labengine" && <LabEngineTab token={token} />}
        {tab === "magazi" && <MagaziTab token={token} />}
        {tab === "portfolios" && <PortfoliosTab token={token} />}
        {tab === "arena" && <ArenaTab token={token} />}
        {tab === "etfs" && <EtfAuditTab token={token} />}
        {tab === "vplab" && <VirtualPortfolioTab token={token} />}

        {/* PORTFOLIO TAB */}
        {tab === "portfolio" && (() => {
          const totalEquity = allBots.reduce((s, b) => s + (b.equity || 0), 0);
          const totalCapital = allBots.reduce((s, b) => s + (b.initial_capital || 0), 0);
          // Prefer lifetime realized from /fleet-stats over per-session
          // heartbeat sum; falls back if fleet-stats unavailable.
          const totalRealizedPnl = fleetStats?.fleet?.total_profit
            ?? allBots.reduce((s, b) => s + (b.total_profit || 0), 0);
          const totalDeals = allBots.reduce((s, b) => s + (b.deals_completed || 0), 0);
          const growth = totalCapital > 0 ? ((totalEquity / totalCapital - 1) * 100) : 0;
          const totalUnrealized = allBots.reduce((s, b) => {
            if (b.in_deal && b.avg_entry_price > 0)
              return s + ((b.current_price || 0) - b.avg_entry_price) * (b.total_qty || 0);
            return s;
          }, 0);
          const totalPnl = totalRealizedPnl + totalUnrealized;
          // Use REAL trailing fleet APY (capital-weighted, backend-computed)
          // instead of the stale 51% DOGE backtest. Falls back to 51% only
          // if there's no actual APY yet.
          const _fleetApy = (fleetStats?.fleet?.actual_apy ?? 51) / 100;
          const estMonthly = allBots.reduce((s, b) => s + (b.equity || b.initial_capital || 0) * _fleetApy / 12, 0);
          const estAnnual = estMonthly * 12;

          return (
            <div className="space-y-6">
              {/* Hero */}
              <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-6">
                <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest mb-2">Total Portfolio Value</div>
                <div className="text-5xl font-black font-mono text-white mb-1">
                  ${totalEquity.toLocaleString(undefined, {minimumFractionDigits:0, maximumFractionDigits:0})}
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <span className={`text-lg font-mono font-bold ${totalPnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)} all-time
                  </span>
                  <span className={`text-sm font-mono ${growth >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    ({growth >= 0 ? "+" : ""}{growth.toFixed(2)}%)
                  </span>
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: "Deployed Capital", value: `$${totalCapital.toLocaleString(undefined,{maximumFractionDigits:0})}`, color: "text-zinc-200" },
                  { label: "Realized P&L", value: `${totalRealizedPnl >= 0 ? "+" : ""}$${totalRealizedPnl.toFixed(2)}`, color: totalRealizedPnl >= 0 ? "text-emerald-400" : "text-red-400" },
                  { label: "Unrealized P&L", value: `${totalUnrealized >= 0 ? "+" : ""}$${totalUnrealized.toFixed(2)}`, color: totalUnrealized >= 0 ? "text-teal-400" : "text-amber-400" },
                  { label: "Total Deals", value: totalDeals, color: "text-blue-400" },
                  { label: "Est. Monthly", value: `$${estMonthly.toFixed(0)}`, color: "text-purple-400" },
                  { label: "Est. Annual", value: `$${estAnnual.toFixed(0)}`, color: "text-purple-400" },
                  { label: "Active Bots", value: `${allBots.filter(b=>b.connected).length} / ${allBots.length}`, color: "text-zinc-200" },
                  { label: "In Deal", value: `${allBots.filter(b=>b.in_deal).length} bots`, color: "text-amber-400" },
                ].map(s => (
                  <div key={s.label} className="bg-zinc-900/60 border border-zinc-800/60 rounded-xl p-3">
                    <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">{s.label}</div>
                    <div className={`text-lg font-mono font-bold ${s.color}`}>{s.value}</div>
                  </div>
                ))}
              </div>

              {/* Per-bot breakdown */}
              <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                  <h3 className="text-sm font-mono font-bold text-zinc-200">Bot Breakdown</h3>
                  <span className="text-xs text-zinc-500">{allBots.length} bots</span>
                </div>
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
                      <th className="text-left px-4 py-2.5">Bot</th>
                      <th className="text-right px-3 py-2.5">Equity</th>
                      <th className="text-right px-3 py-2.5">Realized</th>
                      <th className="text-right px-3 py-2.5">Unrealized</th>
                      <th className="text-right px-3 py-2.5">Deals</th>
                      <th className="text-right px-3 py-2.5">Uptime</th>
                      <th className="text-center px-3 py-2.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allBots.map(bot => {
                      const unrealized = bot.in_deal && bot.avg_entry_price > 0
                        ? ((bot.current_price || 0) - bot.avg_entry_price) * (bot.total_qty || 0) : 0;
                      const realized = bot.total_profit || 0;
                      return (
                        <tr key={bot.bot_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-1.5 h-1.5 rounded-full ${bot.connected ? "bg-emerald-400" : "bg-zinc-600"}`} />
                              <div>
                                <div className="text-zinc-100 font-semibold">{bot.symbol?.replace("USDC","").replace("USDT","")}</div>
                                <div className="text-zinc-600 text-[10px]">{bot.bot_id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right text-zinc-200 font-semibold">${(bot.equity || 0).toLocaleString(undefined,{maximumFractionDigits:0})}</td>
                          <td className={`px-3 py-3 text-right font-semibold ${realized >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {realized >= 0 ? "+" : ""}${realized.toFixed(2)}
                          </td>
                          <td className={`px-3 py-3 text-right ${unrealized >= 0 ? "text-teal-400" : "text-amber-400"}`}>
                            {unrealized !== 0 ? `${unrealized >= 0 ? "+" : ""}$${unrealized.toFixed(2)}` : "—"}
                          </td>
                          <td className="px-3 py-3 text-right text-zinc-400">{bot.deals_completed || 0}</td>
                          <td className="px-3 py-3 text-right text-zinc-500">
                            {bot.uptime_hours != null
                              ? bot.uptime_hours < 24 ? `${bot.uptime_hours}h` : `${(bot.uptime_hours/24).toFixed(1)}d`
                              : "—"}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] border ${
                              bot.in_deal ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                                : bot.connected ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                                : "bg-zinc-700/30 text-zinc-500 border-zinc-700/30"
                            }`}>
                              {bot.in_deal ? "IN DEAL" : bot.connected ? "IDLE" : "OFFLINE"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Compound growth timeline */}
              <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
                <h3 className="text-sm font-mono font-bold text-zinc-200 mb-4">📈 Compound Growth Milestones</h3>
                <div className="space-y-2">
                  {[1, 3, 6, 12, 24].map(months => {
                    const apy = 0.51;
                    const projected = totalEquity * Math.pow(1 + apy, months / 12);
                    const gain = projected - totalEquity;
                    return (
                      <div key={months} className="flex items-center justify-between bg-zinc-800/30 rounded-lg px-3 py-2">
                        <span className="text-xs font-mono text-zinc-400">{months < 12 ? `${months}mo` : `${months/12}yr`}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-xs font-mono text-emerald-400">+${gain.toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                          <span className="text-sm font-mono font-bold text-zinc-100">${projected.toLocaleString(undefined,{maximumFractionDigits:0})}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Quick Close Confirmation Modal */}
        {showCloseConfirm && botStatus?.in_deal && (() => {
          const price = botStatus.current_price || 0;
          const avg = botStatus.avg_entry || 0;
          const pnlPct = avg > 0 ? ((price - avg) / avg * 100) : 0;
          const invested = botStatus.total_invested || 0;
          const estValue = (botStatus.total_qty || 0) * price;
          const estProfit = estValue - invested;
          const isProfit = estProfit >= 0;
          return (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="close-confirm-modal">
              <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
                <h3 className="text-lg font-mono font-bold text-red-400 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Quick Close Deal
                </h3>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm font-mono">
                    <span className="text-zinc-500">Current Price</span>
                    <span className="text-zinc-200">${price.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-mono">
                    <span className="text-zinc-500">Avg Entry</span>
                    <span className="text-zinc-200">${avg.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-mono">
                    <span className="text-zinc-500">Invested</span>
                    <span className="text-zinc-200">${invested.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-mono">
                    <span className="text-zinc-500">SOs Filled</span>
                    <span className="text-zinc-200">{botStatus.safety_orders_filled || 0}/{botStatus.max_safety_orders || 15}</span>
                  </div>
                  <div className="border-t border-zinc-800 pt-3 flex justify-between text-base font-mono font-bold">
                    <span className="text-zinc-400">Est. P&L</span>
                    <span className={isProfit ? "text-emerald-400" : "text-red-400"}>
                      {isProfit ? "+" : ""}${estProfit.toFixed(2)} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%)
                    </span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowCloseConfirm(false)} data-testid="close-confirm-cancel"
                    className="flex-1 px-4 py-2.5 bg-zinc-800 border border-zinc-700 text-zinc-400 rounded-lg font-mono text-sm hover:bg-zinc-700 transition-all">
                    Cancel
                  </button>
                  <button onClick={() => { sendCommand("force_close"); setShowCloseConfirm(false); }} data-testid="close-confirm-execute"
                    className={`flex-1 px-4 py-2.5 border rounded-lg font-mono text-sm font-bold transition-all ${
                      isProfit
                        ? "bg-emerald-600/20 border-emerald-600/40 text-emerald-400 hover:bg-emerald-600/30"
                        : "bg-red-600/20 border-red-600/40 text-red-400 hover:bg-red-600/30"
                    }`}>
                    {isProfit ? "SELL FOR PROFIT" : "SELL AT LOSS"}
                  </button>
                </div>
                <p className="text-[10px] text-zinc-600 mt-3 text-center font-mono">
                  Telegram: /close then /confirm for mobile
                </p>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
